import t from "@/lang/lang";
import { MonitorTypeUtil } from "@/services/backend/qpm/lib/monitor-type-util";
import { MonitorType } from "@/services/backend/qpm/types/enums";
import { ServicePriceListComponent } from "@/routes/processes/[processId]/qpm/calculations/[calculationId]/_components/maintenance-price-list";
import { ServiceContainerComponent } from "@/routes/processes/[processId]/qpm/calculations/[calculationId]/_components/service-cost-component";
import { CalculationServiceType } from "@/services/backend/qpm/calculations/types/calculation-enum";
import { useCalculationData } from "@/services/backend/qpm/calculations/hooks/calculation-context";
import {
  FlyExterminatorMaintenanceCohort,
  FlyExterminatorMaintenanceMonitorViewDTO,
  MaintenancePriceList,
  UpdateMaintenanceLaborPriceReq,
} from "@/services/backend/qpm/calculations/types/calculation";
import {
  FloatNumberInput,
  IntNumberInput,
  Value,
} from "@/shared/components/domain/qpm/inputs/number-input";
import { Label } from "@/shared/components/ui/label";
import { useEffect, useMemo, useState } from "react";
import {
  useQpmCalcUpdateMaintenanceMeasureRateMutation,
  useQpmCalcUpdateMonitorSnapshotConsumableMutation,
} from "@/services/backend/qpm/calculations/api/calculation-api";
import { useDebouncedMutationWithPersistenceStateContextUpdate } from "@/shared/lib/debounce/debounce";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/shared/components/ui/accordion";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/shared/components/ui/tooltip";
import { Info } from "lucide-react";
import { ServiceConsumablesTable } from "@/routes/processes/[processId]/qpm/calculations/[calculationId]/_components/service-consumables-table";

const tooltipContentClassName =
  "bg-background text-foreground max-w-sm border text-left text-xs";

export function ServiceRoute() {
  const { calculation } = useCalculationData();
  const maintenance = normalizeFlyExterminatorMaintenance(
    calculation?.flyExterminatorMaintenance,
  );

  return (
    calculation && (
      <div className="mb-12 overflow-clip rounded-lg border shadow-sm">
        <ServiceContainerComponent
          calculationId={calculation.id}
          calculationServiceType={CalculationServiceType.Maintenance}
          serviceContainer={calculation.maintenanceService}
        />
        <MaintenanceCostFIVComponent
          calculationId={calculation.id}
          maintenanceCohorts={maintenance?.cohorts}
          monitors={maintenance?.monitors}
        />
        <MaintenanceConsumablesFIVComponent
          calculationId={calculation.id}
          monitors={maintenance?.monitors}
        />
      </div>
    )
  );
}

export type MaintenanceCostFIVComponentProps = {
  calculationId: string;
  maintenanceCohorts?: FlyExterminatorMaintenanceCohort[];
  monitors?: FlyExterminatorMaintenanceMonitorViewDTO[];
};

export function MaintenanceCostFIVComponent({
  calculationId,
  maintenanceCohorts,
  monitors,
}: MaintenanceCostFIVComponentProps) {
  const cohorts = maintenanceCohorts ?? [];
  const monitorById = useMemo(() => {
    const entries = (monitors ?? []).map(
      (monitor) => [monitor.monitorId, monitor] as const,
    );
    return Object.fromEntries(entries);
  }, [monitors]);

  return (
    <Accordion
      type="single"
      defaultValue={MonitorType.FlyExterminator}
      collapsible
    >
      <AccordionItem value={MonitorType.FlyExterminator}>
        <AccordionTrigger className="bg-secondary rounded-none border-t px-4 font-bold uppercase">
          {t("Wartungskosten")}{" "}
          {t(MonitorTypeUtil.toString(MonitorType.FlyExterminator))}
        </AccordionTrigger>
        <AccordionContent>
          {cohorts.length === 0 ? (
            <div className="text-muted-foreground px-4 py-3 text-sm">
              {t("Keine FIV-Wartung vorhanden")}
            </div>
          ) : (
            <div className="space-y-4 py-2">
              {cohorts.map((cohort, idx) => (
                <CohortSection
                  key={cohortSectionKey(cohort, idx)}
                  idx={idx}
                  cohort={cohort}
                  calculationId={calculationId}
                  monitors={monitorById}
                />
              ))}
            </div>
          )}
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
}

function cohortSectionKey(
  cohort: FlyExterminatorMaintenanceCohort,
  idx: number,
) {
  const ids = cohort.serviceGroupIds ?? [];
  // Order isn't meaningful; stable-ish key based on sorted ids.
  return `${idx}-${cohort.totalAmountOfMeasures}-${ids.length}-${[...ids].sort().join("-")}`;
}

type CohortSectionProps = {
  idx: number;
  cohort: FlyExterminatorMaintenanceCohort;
  calculationId: string;
  monitors: Record<string, FlyExterminatorMaintenanceMonitorViewDTO>;
};

function CohortSection({
  idx,
  cohort,
  calculationId,
  monitors,
}: CohortSectionProps) {
  const label = `${t("Kohorte")} ${idx + 1} • ${cohort.totalAmountOfMeasures} ${t("Maßnahmen/Jahr")} • ${
    cohort.serviceGroupIds?.length ?? 0
  } ${t("Servicegruppen")}`;

  return (
    <div className="overflow-clip rounded-md border">
      <div className="bg-muted/30 flex items-center justify-between gap-3 px-4 py-2">
        <div className="font-semibold">{label}</div>
        <div className="text-muted-foreground text-xs">
          {t("Label")}: {cohortShortLabel(cohort)}
        </div>
      </div>

      <MaintenanceSummary
        calculationId={calculationId}
        maintenancePriceList={cohort.priceList}
        cohort={cohort}
      />

      <ServicePriceListComponent
        calculationId={calculationId}
        maintenancePriceList={cohort.priceList}
        monitors={monitors}
      />
    </div>
  );
}

function cohortShortLabel(cohort: FlyExterminatorMaintenanceCohort) {
  const measures = cohort.totalAmountOfMeasures ?? 0;
  const groups = cohort.serviceGroupIds?.length ?? 0;
  return `M${measures}-G${groups}`;
}

type MaintenanceSummaryProps = {
  calculationId: string;
  maintenancePriceList?: MaintenancePriceList;
  cohort: FlyExterminatorMaintenanceCohort;
};

function MaintenanceSummary({
  calculationId,
  maintenancePriceList,
  cohort,
}: MaintenanceSummaryProps) {
  if (!maintenancePriceList) return null;

  const measures =
    cohort.totalAmountOfMeasures ?? maintenancePriceList.amountOfMeasures ?? 0;

  return (
    <div className="px-4 py-2">
      <div className="bg-muted/25 grid grid-cols-3 gap-4 rounded-md border p-3">
        <div className="flex items-center justify-start gap-2">
          <Label
            htmlFor={`${calculationId}-maintenance-amount-of-measures-${measures}`}
          >
            {t("Maßnahmen / Jahr")}
          </Label>
          <IntNumberInput
            id={`${calculationId}-maintenance-amount-of-measures-${measures}`}
            value={measures}
            disabled
          />
        </div>

        <LaborCostPerUnitPerMeasure
          calculationId={calculationId}
          laborPricePerDevicePerMeasure={
            cohort.laborPricePerDevicePerMeasure ?? 0
          }
          serverLaborPricePerDevicePerMeasure={
            cohort.laborPricePerDevicePerMeasure ?? 0
          }
          derivedLaborPricePerDevicePerMeasure={
            cohort.calculatedLaborPricePerDevicePerMeasure ??
            maintenancePriceList.calculatedLaborPricePerDevicePerMeasure
          }
        />

        <div className="flex items-center justify-end gap-2">
          <Label
            htmlFor={`${calculationId}-maintenance-total-cost-per-measure-${measures}`}
          >
            {t("Preis FIV-Wartung inkl. Matrial pro Maßnahme")}
          </Label>
          <Value
            id={`${calculationId}-maintenance-total-cost-per-measure-${measures}`}
            value={maintenancePriceList.totalCostPerMeasure ?? 0}
            fractions={2}
          />
          <InfoIconTooltip
            text={`${t("Gesamtpreis pro Jahr")}: ${
              maintenancePriceList.totalCost?.toLocaleString("de-DE", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }) ?? 0
            }`}
          />
        </div>
      </div>
    </div>
  );
}

function InfoIconTooltip({ text, id }: { text: string; id?: string }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          id={id}
          type="button"
          className="text-muted-foreground hover:text-foreground inline-flex items-center"
        >
          <Info className="h-4 w-4" />
        </button>
      </TooltipTrigger>
      <TooltipContent className={tooltipContentClassName}>
        {text}
      </TooltipContent>
    </Tooltip>
  );
}

type LaborCostPerUnitPerMeasureProps = {
  calculationId: string;
  /** Value shown in the input. */
  laborPricePerDevicePerMeasure: number;
  /** The actual server labor value; used for dirty check / loop prevention. */
  serverLaborPricePerDevicePerMeasure: number;
  /** Derived labor value from service groups (display-only). */
  derivedLaborPricePerDevicePerMeasure?: number;
};

function LaborCostPerUnitPerMeasure({
  calculationId,
  laborPricePerDevicePerMeasure,
  serverLaborPricePerDevicePerMeasure,
  derivedLaborPricePerDevicePerMeasure,
}: LaborCostPerUnitPerMeasureProps) {
  const [localValue, setLocalValue] = useState<number>(
    laborPricePerDevicePerMeasure,
  );
  const [hasUserEdited, setHasUserEdited] = useState(false);

  const [updateMeasureRate, measureRateState] =
    useQpmCalcUpdateMaintenanceMeasureRateMutation();

  useEffect(() => {
    // Keep input in sync with changes from server/navigation, but don't mark as edited.
    setLocalValue(laborPricePerDevicePerMeasure);
    setHasUserEdited(false);
  }, [laborPricePerDevicePerMeasure]);

  const req: UpdateMaintenanceLaborPriceReq = {
    calculationId,
    laborPricePerDevicePerMeasure: localValue,
  };

  const shouldSkip =
    !hasUserEdited || localValue === serverLaborPricePerDevicePerMeasure;

  useDebouncedMutationWithPersistenceStateContextUpdate(
    req,
    updateMeasureRate,
    measureRateState.isLoading,
    measureRateState.error,
    measureRateState.isSuccess,
    measureRateState.reset,
    750,
    { skip: shouldSkip },
  );

  const derivedInfo =
    derivedLaborPricePerDevicePerMeasure == null
      ? undefined
      : `${t("Abgeleitet aus Servicegruppen")}: ${derivedLaborPricePerDevicePerMeasure.toLocaleString(
          "de-DE",
          {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          },
        )} €`;

  const infoText = t(
    "Arbeitskosten je FIV und Maßnahme (in €). Preis für die Wartungsarbeit " +
      "an einem einzelnen Fluginsektenvernichter pro Servicetermin. " +
      "Materialkosten sind nicht enthalten.",
  );

  return (
    <div className="flex items-center justify-start gap-2">
      <Label htmlFor={`${calculationId}-maintenance-measure-rate`}>
        {t("Arbeitskosten je")}{" "}
        {MonitorTypeUtil.toString(MonitorType.FlyExterminator)}{" "}
        {t("und Maßnahme")}
      </Label>

      <InfoIconTooltip
        id={`${calculationId}-maintenance-measure-rate-info`}
        text={infoText}
      />

      {derivedInfo && (
        <InfoIconTooltip
          id={`${calculationId}-maintenance-measure-rate-derived-info`}
          text={derivedInfo}
        />
      )}

      {derivedLaborPricePerDevicePerMeasure != null && (
        <button
          type="button"
          className="text-xs underline"
          onClick={() => {
            setHasUserEdited(true);
            setLocalValue(derivedLaborPricePerDevicePerMeasure);
          }}
        >
          {t("Übernehmen")}
        </button>
      )}

      <FloatNumberInput
        id={`${calculationId}-maintenance-measure-rate`}
        value={localValue}
        fractions={2}
        onBlur={(val) => {
          setHasUserEdited(true);
          setLocalValue(val);
        }}
        handleEnterKeyDown={(val) => {
          setHasUserEdited(true);
          setLocalValue(val);
        }}
      />
    </div>
  );
}

export type MaintenanceConsumablesFIVComponentProps = {
  calculationId: string;
  monitors?: FlyExterminatorMaintenanceMonitorViewDTO[];
};

export function MaintenanceConsumablesFIVComponent({
  calculationId,
  monitors,
}: MaintenanceConsumablesFIVComponentProps) {
  const monitorList = monitors ?? [];

  const [updateConsumable] =
    useQpmCalcUpdateMonitorSnapshotConsumableMutation();

  return (
    <Accordion type="single" defaultValue="fiv-consumables" collapsible>
      <AccordionItem value="fiv-consumables">
        <AccordionTrigger className="bg-secondary rounded-none border-t px-4 font-bold uppercase">
          {t(MonitorTypeUtil.toString(MonitorType.FlyExterminator))}{" "}
          {t("Verbrauchsmaterialien")}
        </AccordionTrigger>
        <AccordionContent>
          {monitorList.length === 0 ? (
            <div className="text-muted-foreground px-4 py-3 text-sm">
              {t("Keine Verbrauchsmaterialien vorhanden")}
            </div>
          ) : (
            <div className="space-y-4 py-2">
              {monitorList.map((monitor) => (
                <div
                  key={monitor.monitorId}
                  className="overflow-clip rounded-md border"
                >
                  <div className="bg-muted/30 px-4 py-2 font-semibold">
                    {monitor.name}
                  </div>
                  <div className="px-4 py-2">
                    <ServiceConsumablesTable
                      calculationId={calculationId}
                      monitorId={monitor.monitorId}
                      consumables={monitor.consumables}
                      onSaveConsumable={async (req) => {
                        await updateConsumable(req).unwrap();
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
}

function normalizeFlyExterminatorMaintenance(raw: unknown): {
  cohorts?: FlyExterminatorMaintenanceCohort[];
  monitors?: FlyExterminatorMaintenanceMonitorViewDTO[];
} {
  if (!raw || typeof raw !== "object") {
    return {};
  }

  const record = raw as Record<string, unknown>;
  const cohortsRaw =
    (record.cohorts as unknown[]) ?? (record.Cohorts as unknown[]);
  const monitorsRaw =
    (record.monitors as unknown[]) ?? (record.Monitors as unknown[]);

  const cohorts = Array.isArray(cohortsRaw)
    ? cohortsRaw.map((cohort) => {
        const c = cohort as Record<string, unknown>;
        return {
          totalAmountOfMeasures:
            (c.totalAmountOfMeasures as number) ??
            (c.TotalAmountOfMeasures as number) ??
            0,
          serviceGroupIds:
            (c.serviceGroupIds as string[]) ??
            (c.ServiceGroupIds as string[]) ??
            [],
          priceList:
            (c.priceList as MaintenancePriceList) ??
            (c.PriceList as MaintenancePriceList),
          laborPricePerDevicePerMeasure:
            (c.laborPricePerDevicePerMeasure as number) ??
            (c.LaborPricePerDevicePerMeasure as number) ??
            0,
          calculatedLaborPricePerDevicePerMeasure:
            (c.calculatedLaborPricePerDevicePerMeasure as number) ??
            (c.CalculatedLaborPricePerDevicePerMeasure as number) ??
            0,
        } as FlyExterminatorMaintenanceCohort;
      })
    : undefined;

  const monitors = Array.isArray(monitorsRaw)
    ? (monitorsRaw as FlyExterminatorMaintenanceMonitorViewDTO[])
    : undefined;

  return { cohorts, monitors };
}
